import { z } from 'zod'

import { DateZod } from '../common'
import { AuxRoleZod } from '../regions'

const legacySixStatementAnswers = {
  affirmativeNoCriminalOffenses: z.boolean().nullish(),
  affirmativeCriminalOffenses: z.boolean().nullish(),
  affirmativeNoPendingCharges: z.boolean().nullish(),
  affirmativePendingCharges: z.boolean().nullish(),
  affirmativeNoCourtOrders: z.boolean().nullish(),
  affirmativeCourtOrders: z.boolean().nullish(),
}

export const RegistrationRolesZod = z.enum(['driver', 'escort', 'partner'])

export const RegistrationErrorZod = z.object({
  field: z.string().nullable(),
  message: z.object({ fr: z.string(), en: z.string() }).nullable(),
})

export const RegistrationZod = z.object({
  status: z.enum(['pending', 'submitted', 'completed', 'error']),
  errorMessage: z.string().nullish(),
  errors: z.array(RegistrationErrorZod).nullish().default(null),
  pendingEvent: z.string().nullish(),
  submitRequestedAt: DateZod.nullish().default(null),
  createdAt: DateZod,
  regionId: z.string(),
  campaignId: z.number(),
  email: z.string(),
  mobileNumber: z.string(),
  servicePointId: z.string().nullish().default(null),
  dataCollectionConsent: z
    .object({
      hasConsent: z.boolean().nullish(),
    })
    .nullable(),
  personalInformation: z
    .object({
      firstName: z.string().nullish(),
      lastName: z.string().nullish(),
      birthDate: DateZod.nullish(),
      gender: z.enum(['male', 'female', 'nonBinary']).nullish(),
      preferredLanguage: z.enum(['english', 'french']).nullish(),
    })
    .nullable(),
  address: z
    .object({
      formattedAddress: z.string().nullish(),
      streetAddress: z.string().nullish(),
      city: z.string().nullish(),
      province: z.string().nullish(),
      postalCode: z.string().nullish(),
      apartment: z.string().nullish(),
    })
    .nullable(),
  organization: z
    .object({
      organizationId: z.string().nullish(),
      organizationName: z.string().nullish(),
      hasShareConsent: z.boolean().nullish(),
    })
    .nullable(),
  roles: z
    .object({
      roles: z.array(RegistrationRolesZod).nullish(),
      auxRoles: z.array(AuxRoleZod).nullish(),
      // Absent from a registration an app older than the central roles wrote: Pilote then only
      // replaces the roles that app could show.
      centralRoleIds: z.array(z.number()).nullish(),
    })
    .nullable(),
  licenseAndInsurance: z
    .object({
      licenseNumber: z.string().nullish(),
      licenseExpiryDate: DateZod.nullish(),
      canDriveManual: z.boolean().nullish(),
      insuranceCompany: z.string().nullish(),
      insurancePolicyNumber: z.string().nullish(),
      insuranceExpiryDate: DateZod.nullish(),
    })
    .nullable(),
  teammates: z
    .object({
      teammate1FirstName: z.string().nullish(),
      teammate1LastName: z.string().nullish(),
      teammate2FirstName: z.string().nullish(),
      teammate2LastName: z.string().nullish(),
    })
    .nullable(),
  criminalRecord: z
    .object({
      hasCriminalOffenses: z.boolean().nullish(),
      hasPendingCharges: z.boolean().nullish(),
      hasCourtOrders: z.boolean().nullish(),
      ...legacySixStatementAnswers,
      offenses: z.array(
        z.object({
          description: z.string().nullish(),
          date: DateZod.nullish(),
          location: z.string().nullish(),
        })
      ),
    })
    .nullable(),
  volunteerDuties: z
    .object({
      hasConsent: z.boolean().nullish(),
    })
    .nullable(),
  availabilities: z
    .object({
      availabilities: z.array(DateZod),
    })
    .nullable(),
  additionalAuthorizations: z
    .object({
      hasEmailUpdatesConsent: z.boolean().nullish(),
      hasFutureCampaignsConsent: z.boolean().nullish(),
    })
    .nullable(),
})

export type Registration = z.infer<typeof RegistrationZod>
export type RegistrationRoles = z.infer<typeof RegistrationRolesZod>
export type RegistrationError = z.infer<typeof RegistrationErrorZod>
